import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Keeps the dashboard table filters and pagination in the URL query string so the
 * resulting URL can be used to fetch the data from the backend.
 *
 * URL params: ?search=&country=&createdWithin=&orderBy=&page=
 *
 * Returns:
 *   filters          { search, country, createdWithin, orderBy }
 *   page             current page (number, 1-based)
 *   setFilter(name, value)  updates a single filter (resets page to 1)
 *   setPage(page)    updates pagination only
 *   resetFilters()   clears every filter (keeps pagination reset)
 *   hasActiveFilters whether at least one filter is applied
 */
const FILTER_KEYS = ['search', 'country', 'createdWithin', 'orderBy'];

/**
 * Orden por defecto de la tabla. Se aplica siempre —incluso tras "limpiar filtros"—:
 * `resetFilters` borra el param `orderBy` de la URL y `filters.orderBy` cae de vuelta a
 * este valor, que `useFavoritePlans` sí manda al back.
 */
export const DEFAULT_ORDER_BY = 'createdDate:desc';

export const useDashboardFilters = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const filters = useMemo(() => ({
        search: searchParams.get('search') ?? '',
        country: searchParams.get('country') ?? '',
        createdWithin: searchParams.get('createdWithin') ?? '',
        orderBy: searchParams.get('orderBy') ?? DEFAULT_ORDER_BY,
    }), [searchParams]);

    const page = useMemo(() => Number(searchParams.get('page')) || 1, [searchParams]);

    const applyParams = useCallback((updates, { resetPage = true } = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (value === '' || value === null || value === undefined) next.delete(key);
            else next.set(key, value);
        });
        if (resetPage) next.delete('page');
        setSearchParams(next);
    }, [searchParams, setSearchParams]);

    const setFilter = useCallback((name, value) => applyParams({ [name]: value }), [applyParams]);

    const setPage = useCallback((nextPage) => {
        applyParams({ page: nextPage > 1 ? String(nextPage) : '' }, { resetPage: false });
    }, [applyParams]);

    const resetFilters = useCallback(() => {
        applyParams(FILTER_KEYS.reduce((acc, key) => ({ ...acc, [key]: '' }), {}));
    }, [applyParams]);

    const hasActiveFilters = useMemo(
        () => FILTER_KEYS.some((key) => !!searchParams.get(key)),
        [searchParams]
    );

    return { filters, page, setFilter, setPage, resetFilters, hasActiveFilters };
};

export default useDashboardFilters;
